'use client'

import React from 'react'
import classNames from 'classnames'

import Loading from '../Loading'

interface ButtonBaseProps {
  size?: 'default' | 'sm'
  variant?: 'primary' | 'secondary' | 'white'
  loading?: boolean
  disabled?: boolean
  className?: string
  loadingClassName?: string
  loadingSpinerClassName?: string
}

type ButtonAsButton = ButtonBaseProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined
    target?: undefined
    rel?: undefined
  }

type ButtonAsLink = ButtonBaseProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string
  }

type ButtonProps = ButtonAsButton | ButtonAsLink

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      children,
      size = 'default',
      loading = false,
      disabled = false,
      variant = 'primary',
      className,
      loadingClassName,
      loadingSpinerClassName,
      ...rest
    },
    ref,
  ) => {
    const sizes = {
      default: 'px-8 py-4 text-base font-semibold',
      sm: 'text-xs font-medium px-3 py-2',
    }
    const variants = {
      primary: 'text-white bg-primary',
      secondary: 'bg-gray-600',
      white: 'bg-white border-gray-100 border text-secondary',
    }

    const classes = classNames({
      'inline-flex items-center justify-center focus:outline-none': true,
      [sizes[size]]: true,
      [variants[variant]]: true,
      '!bg-gray-100 !text-white': disabled,
      '!px-12': loading && size === 'default',
      '!px-8': loading && size === 'sm',
      [className || '']: true,
    })

    const content = loading ? (
      <div
        className={classNames({
          'py-3': size === 'default',
          'py-2': size === 'sm',
        })}
      >
        <Loading
          size={size}
          className={loadingClassName || (disabled ? '!text-gray-100' : '')}
          baseClassName={
            loadingSpinerClassName || (disabled ? '!text-primary' : '')
          }
        />
      </div>
    ) : (
      children
    )

    if ('href' in rest && rest.href) {
      const { href, ...anchorRest } = rest as ButtonAsLink
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classes}
          {...anchorRest}
        >
          {content}
        </a>
      )
    }

    const { type = 'button', ...buttonRest } = rest as ButtonAsButton
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        className={classes}
        disabled={disabled || loading}
        {...buttonRest}
      >
        {content}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
